import type { MouseEvent } from "react";
import { Link, type LinkProps, useNavigate, type NavigateOptions } from "react-router-dom";
import { useAuth } from "./auth-client";

const PROTECTED_DETAIL_PATTERNS = [
  /^\/fec\/committees\/[^/]+$/,
];

function normalizePathname(pathname: string): string {
  if (!pathname) {
    return "/";
  }

  if (pathname.length > 1) {
    return pathname.replace(/\/+$/, "");
  }

  return pathname;
}

function extractPathname(targetPath: string): string {
  try {
    return normalizePathname(new URL(targetPath, "https://www.govoptes.test").pathname);
  } catch {
    return normalizePathname(targetPath.split(/[?#]/u, 1)[0] ?? "/");
  }
}

function shouldInterceptNavigation(event: MouseEvent<HTMLAnchorElement>, target: string | undefined): boolean {
  if (event.button !== 0) {
    return false;
  }

  if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
    return false;
  }

  if (target && target !== "_self") {
    return false;
  }

  return true;
}

function buildAuthRedirectPath(basePath: "/sign-in" | "/sign-up", targetPath: string): string {
  return `${basePath}?${new URLSearchParams({ redirect_url: targetPath }).toString()}`;
}

export function isProtectedDetailPath(path: string): boolean {
  const pathname = extractPathname(path);
  return PROTECTED_DETAIL_PATTERNS.some((pattern) => pattern.test(pathname));
}

export function buildSignInRedirectPath(targetPath: string): string {
  return buildAuthRedirectPath("/sign-in", targetPath);
}

export function buildSignUpRedirectPath(targetPath: string): string {
  return buildAuthRedirectPath("/sign-up", targetPath);
}

type ProtectedLinkProps = Omit<LinkProps, "to"> & {
  to: string;
};

export function ProtectedLink({ onClick, target, to, ...props }: ProtectedLinkProps) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented) {
      return;
    }

    if (!shouldInterceptNavigation(event, target)) {
      return;
    }

    if (!isLoaded || isSignedIn || !isProtectedDetailPath(to)) {
      return;
    }

    event.preventDefault();
    navigate(buildSignInRedirectPath(to));
  }

  return <Link {...props} onClick={handleClick} target={target} to={to} />;
}

export function useProtectedNavigation() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();

  return (to: string, options?: NavigateOptions) => {
    const nextPath = isLoaded && !isSignedIn && isProtectedDetailPath(to) ? buildSignInRedirectPath(to) : to;
    navigate(nextPath, options);
  };
}
